import { useEffect, useMemo, useState } from 'react';
import { MVTLayer } from '@deck.gl/geo-layers/typed';
import { useTranslation } from 'react-i18next';
import type { Feature, Geometry } from 'geojson';

import { getI18n } from 'react-i18next';
import { useAppSelector } from 'app/store/store';
import { hexToRGBArray } from 'app/helpers';
import { CheckBoxStateI } from 'app/hooks/useGetMapSettingsState';
import { AreaInfo } from 'ais-facade-api/api';
import { useAlertHook, useZoneSelectHandler } from 'app/hooks';

type PropertiesType = {
  code?: string;
  name?: string;
  color?: string;
  subtype?: string;
};

export const useLayerSpecialAreas = (
  checkBoxSpecialAreasState: CheckBoxStateI,
  selectedAreaId: string | null,
  setSelectedAreaId: React.Dispatch<React.SetStateAction<string | null>>,
) => {
  const { t } = useTranslation();
  const { addAlert } = useAlertHook();
  const handleZoneSelect = useZoneSelectHandler();

  const { baseurl } = window._env_?.AREAS_CONFIG ? JSON.parse(window._env_.AREAS_CONFIG) : { baseurl: '' };
  const url = `https://${baseurl}/map/data/Zones/{z}/{x}/{y}.pbf`;
  const mode = useAppSelector((state) => state.mapStateSlice.mode);
  const isNotBaseMode = mode === 'analytics' || mode === 'playback';
  const { showECAState, showATBAState, showNAVAREAState, showLLZState, showEEZState } = checkBoxSpecialAreasState;
  const [showECA] = showECAState;
  const [showATBA] = showATBAState;
  const [showNAVAREA] = showNAVAREAState;
  const [showLLZ] = showLLZState;
  const [showEEZ] = showEEZState;
  const [clickedZoneId, setClickedZoneId] = useState<string | null>(null);

  useEffect(() => {
    if (!baseurl && (showECA || showATBA || showNAVAREA || showLLZ || showEEZ)) {
      addAlert({
        data: {
          messages: [{ description: t('ErrorOperationOfService'), type: 'error' }],
        },
      });
    }
  }, [showECA, showATBA, showNAVAREA, showLLZ, showEEZ]);

  if (!baseurl) return null;

  return useMemo(() => {
    const generatedLayers = [];
    const defaultFill: [number, number, number, number] = [255, 112, 112, 40];
    const defaultFillSpecial: [number, number, number, number] = [115, 233, 22, 40];
    const defaultStrokeColor: [number, number, number, number] = [255, 255, 255, 190];

    const getSelectedFillColor = (feature: Feature<Geometry, PropertiesType>) => {
      if (feature?.properties?.color) {
        const baseColor = hexToRGBArray(feature.properties.color, 1.0);
        return [...baseColor.slice(0, 3), 120];
      }
      const defaultColor = feature?.properties?.subtype ? defaultFillSpecial : defaultFill;
      return [...defaultColor.slice(0, 3), 120];
    };

    const createLayer = (id: string, layerName: string, visible: boolean) => {
      return new MVTLayer({
        id: `special-areas-mvt-layer-${id}`,
        data: url,
        maxZoom: 10,
        pickable: true,
        autoHighlight: true,
        getFillColor: (f: Feature<Geometry, PropertiesType>) => {
          const zoneId = f?.properties?.code;
          if (zoneId === selectedAreaId) {
            return getSelectedFillColor(f);
          }
          if (f?.properties?.color) {
            return hexToRGBArray(f.properties.color, 0.15);
          }
          return f?.properties?.subtype ? defaultFillSpecial : defaultFill;
        },
        getLineColor: (f: Feature<Geometry, PropertiesType>) => {
          const zoneId = f?.properties?.code;
          if (zoneId === selectedAreaId) {
            return defaultStrokeColor;
          }
          if (f?.properties?.color) {
            return hexToRGBArray(f.properties.color, 0.8);
          }
          return f?.properties?.subtype ? defaultFillSpecial : defaultFill;
        },
        getLineWidth: 2,
        lineWidthUnits: 'pixels',
        loadOptions: { mvt: { layers: [layerName] } },
        highlightColor: (pickingInfo) => {
          const feature = pickingInfo?.object as Feature<Geometry, PropertiesType>;
          if (feature?.properties?.color) {
            const baseColor = hexToRGBArray(feature.properties.color, 1.0);
            return [...baseColor.slice(0, 3), 120];
          }
          const defaultColor = feature?.properties?.subtype ? defaultFillSpecial : defaultFill;
          return [...defaultColor.slice(0, 3), 120];
        },
        visible: visible && !isNotBaseMode,
        onClick: (pickingInfo) => {
          const properties = pickingInfo?.object?.properties;
          const zoneId = properties?.code;
          if (clickedZoneId === zoneId) {
            return;
          }
          setClickedZoneId(zoneId);
          setSelectedAreaId(zoneId);
          if (properties?.name && properties?.code) {
            const language = getI18n().language.split('-')[0].toLowerCase();
            const area: AreaInfo = {
              code: properties.code,
              name: language === 'ru' && properties['name(ru)'] ? properties['name(ru)'] : properties.name,
            };
            handleZoneSelect(area);
          }
        },
        updateTriggers: {
          getFillColor: [selectedAreaId],
          getLineColor: [selectedAreaId],
          getLineWidth: [selectedAreaId],
        },
      });
    };

    if (showECA) generatedLayers.push(createLayer('eca', 'ECA', showECA));
    if (showATBA) generatedLayers.push(createLayer('atba', 'ATBA', showATBA));
    if (showNAVAREA) generatedLayers.push(createLayer('navarea', 'NAVAREAS', showNAVAREA));
    if (showLLZ) generatedLayers.push(createLayer('llz', 'LLZ', showLLZ));
    if (showEEZ) generatedLayers.push(createLayer('eez', 'EEZ', showEEZ));

    return generatedLayers;
  }, [showECA, showATBA, showNAVAREA, showLLZ, showEEZ, isNotBaseMode, selectedAreaId]);
};
